import React from "react";
import { FemaleCalculatorSection } from "@/components/FemaleCalculatorSection";
import { dataSources } from "@/data/demographicData";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { getRouteMeta, canonicalUrl } from "@/seo/routeMeta";

const PATH = "/";

const Index = () => {
  const meta = getRouteMeta(PATH);

  useSEO({
    title: meta?.title ?? "Female Partner Calculator",
    description: meta?.description ?? "",
    keywords: meta?.keywords,
    canonical: canonicalUrl(PATH),
    structuredData: meta?.structuredData,
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <header className="border-b bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Female Partner Calculator
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              How many women actually meet your standards? Calculate your statistical
              probability of finding a compatible female partner using real US Census
              Bureau, CDC, and Pew Research data. Evidence-based compatibility analysis
              with transparent methodology.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Government Data
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Transparent Sources
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Statistical Analysis
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Evaluating standards for men instead?{" "}
              <Link to="/male-partner-calculator" className="text-primary hover:underline font-medium">
                Try the Male Partner Calculator
              </Link>
              {" "}— or explore our{" "}
              <Link to="/ideal-woman-calculator" className="text-primary hover:underline font-medium">
                ideal woman calculator
              </Link>{" "}
              and{" "}
              <Link to="/dating-standards-calculator" className="text-primary hover:underline font-medium">
                dating standards calculator
              </Link>
              .
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 space-y-12">
        {/* How It Works Preview */}
        <section className="text-center space-y-4">
          <h2 className="text-2xl font-semibold">How It Works</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Input your preferences, and our statistical engine calculates probabilities
            using real demographic data from trusted government sources.
          </p>
          <Link
            to="/methodology"
            className="inline-flex items-center text-primary hover:underline"
          >
            Learn about our methodology
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </section>

        <FemaleCalculatorSection sharePath={PATH} />

        {/* Educational Content Preview */}
        <section className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>US Dating Statistics</CardTitle>
              <CardDescription>
                Research-grade articles on the numbers behind the dating pool
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li>• What percentage of US women are single?</li>
                <li>• How your dating pool changes with age</li>
                <li>• Education, income, and height statistics</li>
              </ul>
              <Link to="/stats" className="inline-flex items-center text-primary hover:underline mt-4 text-sm">
                Browse all statistics
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Dating Pool by State</CardTitle>
              <CardDescription>
                How many potential matches live in your state?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li>• Estimated women aged 18-44 per state</li>
                <li>• Education and marriage rates by state</li>
                <li>• All 50 states plus Washington, DC</li>
              </ul>
              <Link to="/states" className="inline-flex items-center text-primary hover:underline mt-4 text-sm">
                Explore state data
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </CardContent>
          </Card>
        </section>

        <section className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
              <CardDescription>
                Common questions about our statistical methodology and data sources
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li>• How accurate are these calculations?</li>
                <li>• What data sources do you use?</li>
                <li>• Should I use this for dating decisions?</li>
              </ul>
              <Link to="/faq" className="inline-flex items-center text-primary hover:underline mt-4 text-sm">
                View all FAQs
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Data Sources</CardTitle>
              <CardDescription>
                Government and research institution data powering our calculations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li>• U.S. Census Bureau (ACS)</li>
                <li>• CDC NHANES Surveys</li>
                <li>• Pew Research Center</li>
              </ul>
              <Link to="/data-sources" className="inline-flex items-center text-primary hover:underline mt-4 text-sm">
                View all sources
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </CardContent>
          </Card>
        </section>

        {/* Data Sources */}
        <section className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">Data Sources</h2>
          <div className="grid gap-4">
            {dataSources.map((source) => (
              <div key={source.id} className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">
                  [{source.id}] {source.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-2">
                  {source.description}
                </p>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline text-sm"
                >
                  {source.url}
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="max-w-4xl mx-auto">
          <div className="border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-r-lg">
            <h3 className="font-semibold mb-4">Statistical Disclaimer</h3>
            <div className="text-sm space-y-2">
              <p>
                This calculator provides statistical probabilities based on
                demographic data and should not be interpreted as definitive
                predictions about individual relationships or dating outcomes.
              </p>
              <p>
                The results reflect population-level statistics and do not
                account for personal factors, geographic location, social
                circles, or individual compatibility beyond the measured
                criteria.
              </p>
              <p>
                Data sources include the U.S. Census Bureau, CDC NHANES surveys,
                Pew Research Center, and other authoritative research
                institutions. All statistics are subject to sampling limitations
                and temporal lag inherent in large-scale demographic surveys.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
